import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useChatWebSocket } from "../hooks/useChatWebSocket";
import { useChatCrypto } from "../hooks/useChatCrypto";
import { ARCHIVE_FEATURE_ENABLED } from "../utils/chatArchive";
import { useCall } from "../hooks/useCall";
import VoiceCallPanel from "../components/VoiceCallPanel";
import PlainTextComposer from "../components/PlainTextComposer";
import { loadGuestSession, clearGuestSession, isGuestSessionLikelyValid } from "./guestSession";
import { createDeviceColorMap, getDeviceColor, getDeviceOrdinal } from "../utils/deviceColors";
import { useMediaQuery } from "../hooks/useMediaQuery";
import { getVideoCallBackground, getVideoCallTextPalette, subscribeVideoCallBackground, } from "../utils/videoCallBackground";
const CALL_KEY_WAIT_MS = 20_000;
const CALL_KEY_RETRY_MS = 2_000;
import { useGuestViewport } from "./useGuestViewport";
const S = {
    page: {
        display: "flex",
        flexDirection: "column",
        height: "100dvh",
        maxWidth: "100vw",
        overflow: "hidden",
        background: "#12141a",
        color: "#e8eaf0",
        fontFamily: "inherit",
        boxSizing: "border-box",
    },
    header: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "10px 16px",
        borderBottom: "1px solid #262a35",
        flexShrink: 0,
        gap: 8,
        flexWrap: "wrap",
    },
    headerLeft: {
        display: "flex",
        flexDirection: "column",
        minWidth: 0,
        overflow: "hidden",
    },
    headerTitle: {
        fontWeight: 700,
        fontSize: 15,
    },
    headerSub: {
        fontSize: 12,
        opacity: 0.65,
        marginTop: 1,
        overflowWrap: "anywhere",
    },
    headerActions: {
        display: "flex",
        gap: 6,
        alignItems: "center",
        flexShrink: 0,
    },
    actionBtn: {
        padding: "7px 11px",
        borderRadius: 14,
        border: "1px solid #333947",
        background: "#1b1e27",
        color: "#e8eaf0",
        cursor: "pointer",
        fontSize: 14,
    },
    logoutBtn: {
        padding: "7px 14px",
        borderRadius: 14,
        border: "1px solid #e05a5a44",
        background: "transparent",
        color: "#e05a5a",
        cursor: "pointer",
        fontSize: 13,
    },
    tabBar: {
        display: "flex",
        borderBottom: "1px solid #262a35",
        flexShrink: 0,
    },
    mainArea: {
        flex: 1,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
    },
    messages: {
        flex: 1,
        overflowY: "auto",
        padding: 16,
        display: "flex",
        flexDirection: "column",
        gap: 8,
    },
    inputBar: {
        display: "flex",
        gap: 8,
        padding: 12,
        borderTop: "1px solid #262a35",
        flexShrink: 0,
        minWidth: 0,
    },
    input: {
        flex: 1,
        minWidth: 0,
        padding: "10px 14px",
        borderRadius: 20,
        border: "1px solid #333947",
        background: "#1b1e27",
        color: "#e8eaf0",
        outline: "none",
        fontSize: 14,
        fontFamily: "inherit",
    },
    sendBtn: {
        padding: "10px 16px",
        borderRadius: 20,
        border: "none",
        background: "#4f6ef7",
        color: "#fff",
        cursor: "pointer",
        fontSize: 14,
    },
    lockedPane: {
        flex: 1,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 8,
        opacity: 0.6,
        padding: 32,
        textAlign: "center",
    },
};
function tabStyle(active) {
    return {
        flex: 1,
        padding: "10px 6px",
        textAlign: "center",
        fontSize: 13,
        fontWeight: active ? 600 : 400,
        color: active ? "#e91e8c" : "#e8eaf0",
        cursor: "pointer",
        background: "transparent",
        border: "none",
        borderBottom: active ? "2px solid #e91e8c" : "2px solid transparent",
        transition: "color 0.2s, border-color 0.2s",
        userSelect: "none",
    };
}
const GuestSendIcon = () => (_jsx("svg", { className: "neon-chat-action-icon", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "1.9", strokeLinecap: "round", strokeLinejoin: "round", "aria-hidden": "true", children: _jsx("path", { d: "M6.5 12h11M13.5 8l4 4-4 4" }) }));
const GuestDashboardPage = () => {
    useGuestViewport();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [session] = useState(() => loadGuestSession());
    const [activeTab, setActiveTab] = useState("chat");
    const [inputText, setInputText] = useState("");
    const [sendFailed, setSendFailed] = useState(false);
    const sendInFlightRef = useRef(false);
    const [videoCallMinimized, setVideoCallMinimized] = useState(false);
    const [desktopCallStatusTarget, setDesktopCallStatusTarget] = useState(null);
    const messagesEndRef = useRef(null);
    /** Stable per-device chat colors (assigned in first-seen order). */
    const deviceColorMapRef = useRef(createDeviceColorMap());
    const isDesktop = useMediaQuery("(min-width: 992px)");
    const videoCallBackground = useSyncExternalStore(subscribeVideoCallBackground, getVideoCallBackground, getVideoCallBackground);
    const videoCallTextPalette = getVideoCallTextPalette(videoCallBackground);
    useEffect(() => {
        if (!isGuestSessionLikelyValid(session)) {
            clearGuestSession();
            navigate("/guest-login", { replace: true });
            return;
        }
        // Chat-only guests must not access the full dashboard shell. Explain why
        // instead of silently bouncing them.
        if (session?.scope !== "full") {
            navigate("/guest-chat", { replace: true, state: { reason: "insufficient_scope" } });
        }
    }, [session, navigate]);
    const room = useMemo(() => session?.room?.toLowerCase() || null, [session]);
    const roomDevicesRef = useRef([]);
    const organizerPresentRef = useRef(false);
    const { encryptFn, decryptFn, handleKeySignal, bindSendSignal, notifyRoomActivity, keyReady, getV2KeyBytes, retryE2EEKey } = useChatCrypto({
        room,
        signMessage: null, // guest: v2 key arrives via ECDH exchange from a wallet device
        enabled: !!room,
        isTrustedResponder: (id, signal) => {
            // STRICT: only a wallet_auth-verified owner device may hand us the key.
            // Prefer the relay's socket-bound attestation on the response itself.
            // Presence remains a compatibility path for an older relay.
            if (signal.senderWalletVerified === true)
                return true;
            const dev = roomDevicesRef.current.find((d) => d.deviceId === id);
            return !!dev && dev.kind === "wallet" && dev.walletVerified === true;
        },
        hasOnlineWalletDevice: () => {
            const devs = roomDevicesRef.current;
            if (organizerPresentRef.current)
                return true;
            if (!devs.length)
                return null; // presence unknown (list not loaded / old relay)
            // Backward-compatible only with the relay's explicit verification bit.
            // Never treat attacker-controlled `kind: wallet` as organizer presence.
            return devs.some((d) => d.walletVerified === true);
        },
    });
    const signalHandlerRef = useRef(null);
    const { messages, sendMessage, isConnected, connectedDevices, roomDevices, organizerPresent, deviceId, sendCallSignal, archiveEnabled, archiveStateUnknown, systemNotice, clearSystemNotice, secretMessages, sendSecretMessage, canSecretMessage, isSecretConversationReady, secretConversationRequests, secretMessageError, secretSafetyNumbers, confirmSecretSafetyNumber, guestAccessClosed } = useChatWebSocket({
        walletAddress: room,
        encryptFn,
        decryptFn,
        onCallSignal: (s) => {
            if (handleKeySignal(s))
                return;
            signalHandlerRef.current?.(s);
        },
        guestToken: session?.sessionJwt || null,
    });
    bindSendSignal(sendCallSignal);
    roomDevicesRef.current = roomDevices;
    organizerPresentRef.current = organizerPresent;
    const verifiedWalletDevices = roomDevices
        .filter((device) => device.kind === "wallet" && device.walletVerified === true)
        .map((device) => device.deviceId)
        .sort()
        .join("|");
    // (Re-)request the room key whenever the connection, room population, or
    // verified-wallet presence changes. Verification can complete without the
    // device count changing.
    useEffect(() => {
        if (isConnected)
            notifyRoomActivity();
    }, [isConnected, connectedDevices, verifiedWalletDevices, notifyRoomActivity]);
    const { callState, callE2EE, participants, incomingFrom, isMuted, isVideoCall, incomingVideo, isCameraOff, localStream, startCall, acceptCall, rejectCall, hangup, leaveCall, toggleMute, toggleCamera, switchCamera, switchMicrophone, isScreenSharing, screenShareDisabled, toggleScreenShare, canRejoin, rejoinCall, rejoinCallId, rejoinCallIdentity, callDropped, dismissCallDropped, handleSignal, fileTransfers, dismissTransfer, callError, dismissCallError, connectionRecovery, ongoingCall, joinOngoingCall, callId, handRaised, toggleHand, raisedHands, connectionPhase, } = useCall({
        deviceId, isConnected, sendCallSignal,
        walletAddress: room, guestToken: session?.sessionJwt || null,
        getE2EEKeyMaterial: getV2KeyBytes,
        e2eeKeyAvailable: keyReady,
    });
    const permanentGuest = useMemo(() => {
        try {
            const encoded = session?.sessionJwt.split(".")[1];
            if (!encoded)
                return false;
            const base64 = encoded.replace(/-/g, "+").replace(/_/g, "/");
            const normalized = base64.padEnd(Math.ceil(base64.length / 4) * 4, "=");
            return JSON.parse(atob(normalized)).inviteKind === "permanent";
        }
        catch {
            return false;
        }
    }, [session?.sessionJwt]);
    const pendingKeyCallIntentRef = useRef(null);
    const pendingKeyCallGenerationRef = useRef(0);
    const pendingKeyCallTimerRef = useRef(null);
    const pendingKeyCallRetryRef = useRef(null);
    const [callKeyWaitStatus, setCallKeyWaitStatus] = useState(null);
    const callStateRef = useRef(callState);
    const callIdRef = useRef(callId);
    const ongoingCallRef = useRef(ongoingCall);
    const canRejoinRef = useRef(canRejoin);
    const rejoinCallIdentityRef = useRef(rejoinCallIdentity);
    const rejoinCallIdRef = useRef(rejoinCallId);
    callStateRef.current = callState;
    callIdRef.current = callId;
    ongoingCallRef.current = ongoingCall;
    canRejoinRef.current = canRejoin;
    rejoinCallIdentityRef.current = rejoinCallIdentity;
    rejoinCallIdRef.current = rejoinCallId;
    const clearPendingKeyCall = useCallback((status = null) => {
        pendingKeyCallGenerationRef.current += 1;
        pendingKeyCallIntentRef.current = null;
        if (pendingKeyCallTimerRef.current)
            clearTimeout(pendingKeyCallTimerRef.current);
        if (pendingKeyCallRetryRef.current)
            clearInterval(pendingKeyCallRetryRef.current);
        pendingKeyCallTimerRef.current = null;
        pendingKeyCallRetryRef.current = null;
        setCallKeyWaitStatus(status);
    }, []);
    const queueUntilKey = useCallback((kind, action) => {
        if (!permanentGuest || keyReady) {
            action();
            return;
        }
        const expectedState = kind === "accept" ? "incoming" : "idle";
        const intendedCallId = kind === "ongoing"
            ? ongoingCallRef.current?.callId
            : kind === "rejoin"
                ? rejoinCallIdRef.current
                : callIdRef.current;
        if (!intendedCallId || callStateRef.current !== expectedState)
            return;
        if (kind === "rejoin" && (!canRejoinRef.current || !rejoinCallIdentityRef.current))
            return;
        clearPendingKeyCall();
        const generation = pendingKeyCallGenerationRef.current + 1;
        pendingKeyCallGenerationRef.current = generation;
        pendingKeyCallIntentRef.current = {
            kind, callId: intendedCallId, expectedState, generation, expiresAt: Date.now() + CALL_KEY_WAIT_MS,
            ...(kind === "rejoin" ? { rejoinIdentity: rejoinCallIdentityRef.current } : {}),
        };
        setCallKeyWaitStatus("waiting");
        const request = () => {
            const pending = pendingKeyCallIntentRef.current;
            if (!pending || pending.generation !== generation || Date.now() >= pending.expiresAt) {
                if (pending?.generation === generation)
                    clearPendingKeyCall("timeout");
                return;
            }
            void retryE2EEKey().then((result) => {
                if (pendingKeyCallIntentRef.current?.generation !== generation)
                    return;
                if (result === "no-wallet-device")
                    clearPendingKeyCall("no-organizer");
                else if (result === "failed")
                    clearPendingKeyCall("failed");
            }).catch(() => {
                if (pendingKeyCallIntentRef.current?.generation === generation)
                    clearPendingKeyCall("failed");
            });
        };
        request();
        pendingKeyCallRetryRef.current = setInterval(request, CALL_KEY_RETRY_MS);
        pendingKeyCallTimerRef.current = setTimeout(() => {
            if (pendingKeyCallIntentRef.current?.generation === generation)
                clearPendingKeyCall("timeout");
        }, CALL_KEY_WAIT_MS);
    }, [clearPendingKeyCall, keyReady, permanentGuest, retryE2EEKey]);
    useEffect(() => {
        const pending = pendingKeyCallIntentRef.current;
        if (!pending)
            return;
        if (guestAccessClosed) {
            clearPendingKeyCall(null);
            return;
        }
        // A ring/end/replacement must never be revived by a late key.
        const stillValid = Date.now() < pending.expiresAt &&
            callStateRef.current === pending.expectedState &&
            (pending.kind === "ongoing"
                ? ongoingCallRef.current?.callId === pending.callId
                : pending.kind === "rejoin"
                    ? canRejoinRef.current && rejoinCallIdentityRef.current === pending.rejoinIdentity
                    : callIdRef.current === pending.callId);
        if (!stillValid) {
            clearPendingKeyCall(null);
            return;
        }
        if (!keyReady)
            return;
        // Consume before invoking: a repeated keyReady render cannot connect twice.
        clearPendingKeyCall(null);
        if (pending.kind === "accept")
            void acceptCall();
        else if (pending.kind === "ongoing")
            void joinOngoingCall();
        else
            void rejoinCall();
    }, [keyReady, callState, callId, ongoingCall, canRejoin, rejoinCallIdentity, guestAccessClosed, acceptCall, clearPendingKeyCall, joinOngoingCall, rejoinCall]);
    useEffect(() => {
        return () => clearPendingKeyCall(null);
    }, [clearPendingKeyCall, room, session?.sessionJwt]);
    useEffect(() => {
        signalHandlerRef.current = handleSignal;
    }, [handleSignal]);
    // Guests never initiate calls, and they don't need organizer approval to
    // join — but joining must come from a CLICK: browsers only show the
    // camera/mic permission prompt in response to a user gesture, so a fully
    // automatic join gets silently denied and the guest becomes a black tile.
    // Incoming calls surface the standard Accept overlay; an already-ongoing
    // call surfaces a one-tap "Join" bar (see VoiceCallPanel props below).
    useEffect(() => {
        if (activeTab === "chat") {
            messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
        }
    }, [messages, activeTab]);
    const handleSend = async () => {
        if (sendInFlightRef.current)
            return;
        const draft = inputText;
        const trimmed = inputText.trim();
        if (!trimmed)
            return;
        sendInFlightRef.current = true;
        setSendFailed(false);
        try {
            await sendMessage(trimmed);
            setInputText((current) => current === draft ? "" : current);
        }
        catch {
            setSendFailed(true);
        }
        finally {
            sendInFlightRef.current = false;
        }
    };
    const handleLogout = () => {
        // Safety net: leaving kills the guest session (chat + call). A mere call
        // interruption belongs on the red hang-up button.
        if (!window.confirm(t("magicLink.guest.leaveConfirm")))
            return;
        clearGuestSession();
        navigate("/guest-login", { replace: true });
    };
    // Scheduler auto-closed this ad-hoc conference (a scheduled meeting is
    // starting): end any running call locally — the invites are revoked.
    useEffect(() => {
        if (systemNotice?.notice === "conference_closed_for_meeting" && callState !== "idle") {
            hangup();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [systemNotice]);
    if (!session) {
        return (_jsx("div", { style: { ...S.page, alignItems: "center", justifyContent: "center" }, children: t("magicLink.guest.verifying") }));
    }
    if (guestAccessClosed) {
        return (_jsx("div", { style: { ...S.page, alignItems: "center", justifyContent: "center", padding: 24, textAlign: "center" }, role: "alert", children: t("magicLink.errors.adminNotPresent") }));
    }
    const formatTime = (ts) => new Date(ts).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    const connectionLabel = isConnected
        ? t("magicLink.guest.connected", { count: connectedDevices })
        : t("magicLink.guest.connecting");
    // During a desktop video call, dock the page into the right-hand column the
    // video overlay leaves free (same pattern as GuestChatPage / the chat modal).
    const videoCallActive = callState === "in-call" && isVideoCall;
    const ongoingJoinVisible = callState === "idle" && !canRejoin && !!ongoingCall;
    // Keep the desktop shell's original surface. The background token and
    // mobile call-shell variables are deliberately applied only to the mobile
    // branch below; otherwise the mobile theme leaks into the desktop dock.
    const desktopPageStyle = videoCallActive
        ? {
            ...S.page,
            position: "fixed",
            top: 0,
            right: 0,
            bottom: 0,
            left: "auto",
            width: "clamp(320px, 28vw, 400px)",
            height: "100dvh",
            zIndex: 99991,
            borderLeft: 0,
            backgroundColor: videoCallBackground,
            "--voice-call-surface-background": videoCallBackground,
            "--voice-call-text-primary": videoCallTextPalette.primary,
            "--voice-call-text-muted": videoCallTextPalette.muted,
        }
        : S.page;
    const mobilePageStyle = {
        ...S.page,
        background: videoCallBackground,
        backgroundColor: videoCallBackground,
        "--voice-call-surface-background": videoCallBackground,
        "--voice-call-text-primary": videoCallTextPalette.primary,
        "--voice-call-text-muted": videoCallTextPalette.muted,
    };
    return (_jsxs(_Fragment, { children: [videoCallActive &&
                isDesktop &&
                createPortal(_jsx("div", { className: "desktop-video-call-header-divider", style: { "--voice-call-surface-background": videoCallBackground }, "aria-hidden": "true" }), document.body), isDesktop ? (_jsxs("div", { className: `guest-call-page${videoCallActive ? " video-call-active" : ""}`, style: desktopPageStyle, children: [_jsxs("div", { className: "guest-call-header", style: S.header, children: [_jsxs("div", { className: "guest-call-header-identity", style: S.headerLeft, children: [_jsxs("div", { className: "guest-call-header-title-row", children: [_jsxs("div", { style: S.headerTitle, children: ["\uD83D\uDD10 ", t("magicLink.guest.dashboardTitle")] }), videoCallActive && (_jsxs("span", { className: `guest-call-e2ee-badge ${callE2EE === true || callE2EE === "p2p"
                                                    ? "secure"
                                                    : callE2EE === "partial"
                                                        ? "partial"
                                                        : callE2EE === false
                                                            ? "off"
                                                            : "pending"}`, title: callE2EE === true
                                                    ? t("mobile.chat.call.e2eeOn")
                                                    : callE2EE === "p2p"
                                                        ? t("mobile.chat.call.e2eeP2p")
                                                        : callE2EE === "partial"
                                                            ? t("mobile.chat.call.e2eeMixed")
                                                            : callE2EE === false
                                                                ? t("mobile.chat.call.e2eeOff")
                                                                : t("mobile.chat.call.e2eeVideoSecuringTitle"), children: [_jsx("i", { className: `ti ${callE2EE === true || callE2EE === "p2p" ? "ti-shield-check" : "ti-lock"}`, "aria-hidden": "true" }), callE2EE === true || callE2EE === "p2p"
                                                        ? "E2E"
                                                        : callE2EE === "partial"
                                                            ? t("mobile.chat.call.e2eeMixedShort")
                                                            : callE2EE === false
                                                                ? t("mobile.chat.call.e2eeOffShort")
                                                                : "E2E…"] }))] }), _jsxs("div", { style: S.headerSub, children: [session.email, " \u00B7 ", connectionLabel] })] }), _jsx("div", { className: "guest-call-header-actions", style: S.headerActions, children: _jsx("button", { className: "guest-call-leave", style: S.logoutBtn, onClick: handleLogout, children: t("magicLink.guest.logout") }) })] }), videoCallActive && (_jsx("div", { className: "guest-desktop-call-status-slot", ref: setDesktopCallStatusTarget })), _jsx("div", { className: "guest-call-main", style: S.mainArea, children: activeTab === "chat" ? (_jsxs(_Fragment, { children: [systemNotice && (_jsxs("div", { style: {
                                        display: "flex", alignItems: "center", gap: 10,
                                        padding: "8px 16px", flexShrink: 0, fontSize: 12,
                                        background: systemNotice.notice === "conference_closed_for_meeting" ? "#2a1c16" : "#2a2416",
                                        borderBottom: "1px solid #4d431c",
                                        color: systemNotice.notice === "conference_closed_for_meeting" ? "#e6944d" : "#e6b34d",
                                    }, children: [_jsx("span", { "aria-hidden": true, children: systemNotice.notice === "conference_closed_for_meeting" ? "⏹" : "⏰" }), _jsx("span", { style: { flex: 1 }, children: systemNotice.notice === "conference_closed_for_meeting"
                                                ? t("mobile.chat.notice.conference_closed")
                                                : t(systemNotice.meetingTitle ? "mobile.chat.notice.meeting_imminent" : "mobile.chat.notice.meeting_imminent_untitled", {
                                                    title: systemNotice.meetingTitle || "",
                                                    time: systemNotice.startsAt
                                                        ? new Date(systemNotice.startsAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
                                                        : "",
                                                }) }), _jsx("button", { onClick: clearSystemNotice, style: { background: "none", border: "none", color: "inherit", cursor: "pointer", fontSize: 14 }, "aria-label": "close", children: "\u2715" })] })), ARCHIVE_FEATURE_ENABLED && archiveEnabled && (_jsxs("div", { style: {
                                        display: "flex", alignItems: "center", gap: 10,
                                        padding: "8px 16px", flexShrink: 0, fontSize: 12,
                                        background: "#2a2416", borderBottom: "1px solid #4d431c", color: "#e6b34d",
                                    }, children: [_jsx("span", { "aria-hidden": true, children: "\uD83D\uDDC4" }), _jsx("span", { style: { flex: 1 }, children: t("magicLink.guest.archiveActive") })] })), ARCHIVE_FEATURE_ENABLED && !archiveEnabled && archiveStateUnknown && (_jsxs("div", { style: {
                                        display: "flex", alignItems: "center", gap: 10,
                                        padding: "8px 16px", flexShrink: 0, fontSize: 12,
                                        background: "#2a1c16", borderBottom: "1px solid #4d2f1c", color: "#e6944d",
                                    }, children: [_jsx("span", { "aria-hidden": true, children: "\u26A0\uFE0F" }), _jsx("span", { style: { flex: 1 }, children: t("magicLink.guest.archiveUnknown") })] })), _jsxs("div", { className: "guest-call-messages", style: S.messages, children: [messages.length === 0 && (_jsx("div", { className: "guest-call-empty", style: { opacity: 0.6, textAlign: "center", marginTop: 32 }, children: t("magicLink.guest.emptyChat") })), messages.map((m) => {
                                            const color = getDeviceColor(deviceColorMapRef.current, m.deviceId);
                                            const ordinal = getDeviceOrdinal(deviceColorMapRef.current, m.deviceId);
                                            return (_jsxs("div", { className: `guest-call-bubble ${m.isMine ? "mine" : "theirs"}`, style: {
                                                    alignSelf: m.isMine ? "flex-end" : "flex-start",
                                                    maxWidth: "75%",
                                                    padding: "8px 12px",
                                                    borderRadius: 14,
                                                    background: m.isMine ? "#4f6ef7" : "#232733",
                                                    color: "#fff",
                                                    wordBreak: "break-word",
                                                    borderLeft: m.isMine ? undefined : `3px solid ${color}`,
                                                }, children: [!m.isMine && (_jsx("div", { className: "guest-call-device-label", style: { fontSize: 10, fontWeight: 600, color, marginBottom: 2 }, children: t("mobile.chat.call.device", { id: String(ordinal) }) })), _jsx("div", { className: "guest-call-bubble-text", style: m.failedDecrypt ? { fontStyle: "italic", opacity: 0.6 } : undefined, children: m.failedDecrypt ? t("magicLink.guest.undecryptable") : m.text }), _jsx("div", { className: "guest-call-bubble-time", style: { fontSize: 10, opacity: 0.7, textAlign: "right", marginTop: 2 }, children: formatTime(m.timestamp) })] }, m.id));
                                        }), _jsx("div", { ref: messagesEndRef })] }), isConnected && !keyReady && (_jsxs("div", { style: { padding: "6px 12px", fontSize: 12, color: "#e6b34d", background: "#2a2416", borderTop: "1px solid #262a35", flexShrink: 0 }, children: ["\uD83D\uDD10 ", t("magicLink.guest.waitingKey")] })), callKeyWaitStatus && (_jsxs("div", { role: "status", style: { padding: "6px 12px", fontSize: 12, color: "#e6b34d", background: "#2a2416", borderTop: "1px solid #262a35", flexShrink: 0 }, children: ["\uD83D\uDD10 ", callKeyWaitStatus === "waiting"
                                            ? (organizerPresent ? t("magicLink.guest.waitingKey") : t("magicLink.errors.adminNotPresent"))
                                            : callKeyWaitStatus === "timeout"
                                                ? "Secure call key timed out. Try joining again."
                                                : callKeyWaitStatus === "no-organizer"
                                                    ? t("magicLink.errors.adminNotPresent")
                                                    : "Could not request the secure call key. Try again."] })), sendFailed && _jsx("div", { role: "alert", children: t("mobile.chat.send_failed") }), _jsxs("div", { className: "guest-call-composer", style: S.inputBar, children: [_jsx(PlainTextComposer, { className: "guest-call-input", style: S.input, value: inputText, disabled: !isConnected || !keyReady, placeholder: keyReady
                                                ? t("magicLink.guest.inputPlaceholder")
                                                : t("magicLink.guest.waitingKeyShort"), onChange: setInputText, onSend: handleSend }), _jsx("button", { className: "guest-call-send", style: S.sendBtn, onClick: handleSend, disabled: !isConnected || !keyReady || !inputText.trim(), "aria-label": t("magicLink.guest.send"), children: _jsx(GuestSendIcon, {}) })] })] })) : (_jsxs("div", { style: S.lockedPane, children: [_jsx("div", { style: { fontSize: 40 }, children: "\uD83D\uDD12" }), _jsx("div", { style: { fontWeight: 600, fontSize: 15 }, children: t("magicLink.guest.lockedSection") }), _jsx("div", { style: { fontSize: 13, maxWidth: 320 }, children: t("magicLink.guest.lockedSectionHint") })] })) }), _jsx(VoiceCallPanel, { callState: callState, participants: participants, incomingFrom: incomingFrom, isMuted: isMuted, isVideoCall: isVideoCall, incomingVideo: incomingVideo, isCameraOff: isCameraOff, localStream: localStream, onAccept: () => queueUntilKey("accept", () => { void acceptCall(); }), onReject: () => { clearPendingKeyCall(null); rejectCall(); }, onHangup: () => { clearPendingKeyCall(null); hangup(); }, onLeaveCall: () => { clearPendingKeyCall(null); leaveCall(); }, onToggleMute: toggleMute, handRaised: handRaised, onToggleHand: toggleHand, raisedHands: raisedHands, onToggleCamera: toggleCamera, onSwitchCamera: switchCamera, onSwitchMicrophone: switchMicrophone, isScreenSharing: isScreenSharing, screenShareDisabled: screenShareDisabled, onToggleScreenShare: toggleScreenShare, canRejoin: canRejoin, onRejoin: () => queueUntilKey("rejoin", () => { void rejoinCall(); }), callDropped: callDropped, e2ee: callE2EE, onDismissDropped: dismissCallDropped, chatSidebar: isDesktop, desktopStatusTarget: desktopCallStatusTarget, fileTransfers: fileTransfers, onDismissTransfer: dismissTransfer, callError: callError, onDismissCallError: dismissCallError, connectionRecovery: connectionRecovery, connectionPhase: connectionPhase, ongoingJoinAvailable: !!ongoingCall, onJoinOngoing: () => queueUntilKey("ongoing", () => { void joinOngoingCall(); }), secretMessages: secretMessages, onSendSecretMessage: sendSecretMessage, canSecretMessage: canSecretMessage, isSecretConversationReady: isSecretConversationReady, secretConversationRequests: secretConversationRequests, secretMessageError: secretMessageError, secretSafetyNumbers: secretSafetyNumbers, onConfirmSecretSafetyNumber: confirmSecretSafetyNumber, onRetryE2EE: keyReady ? undefined : retryE2EEKey })] })) : (_jsxs("div", { className: `guest-call-page mobile-layout${videoCallActive && isDesktop ? " video-call-active" : ""}${videoCallMinimized ? " mobile-layout--call-minimized" : ""}${ongoingJoinVisible ? " guest-call-page--ongoing-join" : ""}`, style: mobilePageStyle, children: [_jsxs("div", { className: "guest-call-header mobile-chat-header mobile-chat-header--idle-redesign", style: S.header, children: [_jsx("div", { className: "guest-call-header-actions mobile-chat-header-right", style: S.headerActions, children: _jsx("button", { className: "guest-call-leave mobile-chat-action-btn guest-chat-leave-action", style: S.logoutBtn, onClick: handleLogout, title: t("magicLink.guest.logout"), "aria-label": t("magicLink.guest.logout"), children: _jsx("i", { className: "ti ti-logout", "aria-hidden": "true" }) }) }), _jsxs("div", { className: "guest-call-header-identity mobile-chat-header-left", style: S.headerLeft, children: [_jsxs("div", { className: "guest-call-header-summary", children: [_jsxs("h1", { className: "mobile-chat-title", children: [_jsx("i", { className: "ti ti-shield-check", "aria-hidden": "true" }), t("mobile.chat.title")] }), _jsxs("div", { className: "mobile-chat-status", children: [_jsx("span", { className: `mobile-chat-status-dot${isConnected ? " connected" : ""}` }), _jsx("i", { className: "ti ti-users mobile-chat-status-icon", "aria-hidden": "true" }), _jsx("span", { className: "mobile-chat-status-text", children: connectionLabel })] })] }), _jsx("div", { className: "mobile-chat-security-row", children: _jsxs("span", { className: `mobile-chat-encryption-badge guest-call-e2ee-badge ${callE2EE === true || callE2EE === "p2p"
                                                ? "secure"
                                                : callE2EE === "partial"
                                                    ? "partial"
                                                    : callE2EE === false
                                                        ? "off"
                                                        : "pending"}`, title: callE2EE === true
                                                ? t("mobile.chat.call.e2eeOn")
                                                : callE2EE === "p2p"
                                                    ? t("mobile.chat.call.e2eeP2p")
                                                    : callE2EE === "partial"
                                                        ? t("mobile.chat.call.e2eeMixed")
                                                        : callE2EE === false
                                                            ? t("mobile.chat.call.e2eeOff")
                                                            : t("mobile.chat.call.e2eeVideoSecuringTitle"), children: [_jsx("i", { className: `ti ${callE2EE === true || callE2EE === "p2p" ? "ti-shield-check" : "ti-lock"}`, "aria-hidden": "true" }), callE2EE === true || callE2EE === "p2p"
                                                    ? "E2E"
                                                    : callE2EE === "partial"
                                                        ? t("mobile.chat.call.e2eeMixedShort")
                                                        : callE2EE === false
                                                            ? t("mobile.chat.call.e2eeOffShort")
                                                            : "E2E…"] }) })] })] }), _jsx("div", { className: "mobile-content guest-call-main", style: { ...S.mainArea, paddingBottom: 0 }, children: _jsx("div", { className: "mobile-chat", children: activeTab === "chat" ? (_jsxs(_Fragment, { children: [systemNotice && (_jsxs("div", { style: {
                                            display: "flex", alignItems: "center", gap: 10,
                                            padding: "8px 16px", flexShrink: 0, fontSize: 12,
                                            background: systemNotice.notice === "conference_closed_for_meeting" ? "#2a1c16" : "#2a2416",
                                            borderBottom: "1px solid #4d431c",
                                            color: systemNotice.notice === "conference_closed_for_meeting" ? "#e6944d" : "#e6b34d",
                                        }, children: [_jsx("span", { "aria-hidden": true, children: systemNotice.notice === "conference_closed_for_meeting" ? "⏹" : "⏰" }), _jsx("span", { style: { flex: 1 }, children: systemNotice.notice === "conference_closed_for_meeting"
                                                    ? t("mobile.chat.notice.conference_closed")
                                                    : t(systemNotice.meetingTitle ? "mobile.chat.notice.meeting_imminent" : "mobile.chat.notice.meeting_imminent_untitled", {
                                                        title: systemNotice.meetingTitle || "",
                                                        time: systemNotice.startsAt
                                                            ? new Date(systemNotice.startsAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
                                                            : "",
                                                    }) }), _jsx("button", { onClick: clearSystemNotice, style: { background: "none", border: "none", color: "inherit", cursor: "pointer", fontSize: 14 }, "aria-label": "close", children: "\u2715" })] })), ARCHIVE_FEATURE_ENABLED && archiveEnabled && (_jsxs("div", { style: {
                                            display: "flex", alignItems: "center", gap: 10,
                                            padding: "8px 16px", flexShrink: 0, fontSize: 12,
                                            background: "#2a2416", borderBottom: "1px solid #4d431c", color: "#e6b34d",
                                        }, children: [_jsx("span", { "aria-hidden": true, children: "\uD83D\uDDC4" }), _jsx("span", { style: { flex: 1 }, children: t("magicLink.guest.archiveActive") })] })), ARCHIVE_FEATURE_ENABLED && !archiveEnabled && archiveStateUnknown && (_jsxs("div", { style: {
                                            display: "flex", alignItems: "center", gap: 10,
                                            padding: "8px 16px", flexShrink: 0, fontSize: 12,
                                            background: "#2a1c16", borderBottom: "1px solid #4d2f1c", color: "#e6944d",
                                        }, children: [_jsx("span", { "aria-hidden": true, children: "\u26A0\uFE0F" }), _jsx("span", { style: { flex: 1 }, children: t("magicLink.guest.archiveUnknown") })] })), _jsxs("div", { className: "guest-call-messages mobile-chat-messages", style: S.messages, children: [messages.length === 0 && (_jsx("div", { className: "guest-call-empty", style: { opacity: 0.6, textAlign: "center", marginTop: 32 }, children: t("magicLink.guest.emptyChat") })), messages.map((m) => {
                                                const color = getDeviceColor(deviceColorMapRef.current, m.deviceId);
                                                const ordinal = getDeviceOrdinal(deviceColorMapRef.current, m.deviceId);
                                                return (_jsxs("div", { className: `guest-call-bubble ${m.isMine ? "mine" : "theirs"}`, style: {
                                                        alignSelf: m.isMine ? "flex-end" : "flex-start",
                                                        maxWidth: "75%",
                                                        padding: "8px 12px",
                                                        borderRadius: 14,
                                                        background: m.isMine ? "#4f6ef7" : "#232733",
                                                        color: "#fff",
                                                        wordBreak: "break-word",
                                                        borderLeft: m.isMine ? undefined : `3px solid ${color}`,
                                                    }, children: [!m.isMine && (_jsx("div", { className: "guest-call-device-label", style: { fontSize: 10, fontWeight: 600, color, marginBottom: 2 }, children: t("mobile.chat.call.device", { id: String(ordinal) }) })), _jsx("div", { className: "guest-call-bubble-text", style: m.failedDecrypt ? { fontStyle: "italic", opacity: 0.6 } : undefined, children: m.failedDecrypt ? t("magicLink.guest.undecryptable") : m.text }), _jsx("div", { className: "guest-call-bubble-time", style: { fontSize: 10, opacity: 0.7, textAlign: "right", marginTop: 2 }, children: formatTime(m.timestamp) })] }, m.id));
                                            }), _jsx("div", { ref: messagesEndRef })] }), isConnected && !keyReady && (_jsxs("div", { style: { padding: "6px 12px", fontSize: 12, color: "#e6b34d", background: "#2a2416", borderTop: "1px solid #262a35", flexShrink: 0 }, children: ["\uD83D\uDD10 ", t("magicLink.guest.waitingKey")] })), callKeyWaitStatus && (_jsxs("div", { role: "status", style: { padding: "6px 12px", fontSize: 12, color: "#e6b34d", background: "#2a2416", borderTop: "1px solid #262a35", flexShrink: 0 }, children: ["\uD83D\uDD10 ", callKeyWaitStatus === "waiting"
                                                ? (organizerPresent ? t("magicLink.guest.waitingKey") : t("magicLink.errors.adminNotPresent"))
                                                : callKeyWaitStatus === "timeout"
                                                    ? "Secure call key timed out. Try joining again."
                                                    : callKeyWaitStatus === "no-organizer"
                                                        ? t("magicLink.errors.adminNotPresent")
                                                        : "Could not request the secure call key. Try again."] })), sendFailed && _jsx("div", { role: "alert", children: t("mobile.chat.send_failed") }), _jsxs("div", { className: "guest-call-composer mobile-chat-input-bar", style: S.inputBar, children: [_jsx(PlainTextComposer, { className: "guest-call-input mobile-chat-input", style: S.input, value: inputText, disabled: !isConnected || !keyReady, placeholder: keyReady
                                                    ? t("magicLink.guest.inputPlaceholder")
                                                    : t("magicLink.guest.waitingKeyShort"), onChange: setInputText, onSend: handleSend }), _jsx("button", { className: "guest-call-send mobile-chat-send-btn", style: S.sendBtn, onClick: handleSend, disabled: !isConnected || !keyReady || !inputText.trim(), "aria-label": t("magicLink.guest.send"), children: _jsx(GuestSendIcon, {}) })] })] })) : (_jsxs("div", { style: S.lockedPane, children: [_jsx("div", { style: { fontSize: 40 }, children: "\uD83D\uDD12" }), _jsx("div", { style: { fontWeight: 600, fontSize: 15 }, children: t("magicLink.guest.lockedSection") }), _jsx("div", { style: { fontSize: 13, maxWidth: 320 }, children: t("magicLink.guest.lockedSectionHint") })] })) }) }), _jsx("div", { className: `mobile-call-bar-host${videoCallMinimized ? " call-minimized" : ""}${ongoingJoinVisible ? " ongoing-join-present" : ""}`, children: _jsx(VoiceCallPanel, { callState: callState, participants: participants, incomingFrom: incomingFrom, isMuted: isMuted, isVideoCall: isVideoCall, incomingVideo: incomingVideo, isCameraOff: isCameraOff, localStream: localStream, onAccept: () => queueUntilKey("accept", () => { void acceptCall(); }), onReject: () => { clearPendingKeyCall(null); rejectCall(); }, onHangup: () => { clearPendingKeyCall(null); hangup(); }, onLeaveCall: () => { clearPendingKeyCall(null); leaveCall(); }, onToggleMute: toggleMute, handRaised: handRaised, onToggleHand: toggleHand, raisedHands: raisedHands, onToggleCamera: toggleCamera, onSwitchCamera: switchCamera, onSwitchMicrophone: switchMicrophone, isScreenSharing: isScreenSharing, screenShareDisabled: screenShareDisabled, onToggleScreenShare: toggleScreenShare, canRejoin: canRejoin, onRejoin: () => queueUntilKey("rejoin", () => { void rejoinCall(); }), callDropped: callDropped, e2ee: callE2EE, onDismissDropped: dismissCallDropped, chatSidebar: isDesktop, fileTransfers: fileTransfers, onDismissTransfer: dismissTransfer, callError: callError, onDismissCallError: dismissCallError, connectionRecovery: connectionRecovery, connectionPhase: connectionPhase, ongoingJoinAvailable: !!ongoingCall, onJoinOngoing: () => queueUntilKey("ongoing", () => { void joinOngoingCall(); }), secretMessages: secretMessages, onSendSecretMessage: sendSecretMessage, canSecretMessage: canSecretMessage, isSecretConversationReady: isSecretConversationReady, secretConversationRequests: secretConversationRequests, secretMessageError: secretMessageError, secretSafetyNumbers: secretSafetyNumbers, onConfirmSecretSafetyNumber: confirmSecretSafetyNumber, onRetryE2EE: keyReady ? undefined : retryE2EEKey, onMinimizedChange: setVideoCallMinimized }) })] }))] }));
};
export default GuestDashboardPage;
